// An offered job, rendered from the backbone case in the installer's words.
// The six questions are here in this order — what will happen · why you ·
// options · if you say no · undo — but there is no approval language, no kind
// chip, no desk name, no "EmmA drafted": the office offers, the installer answers.
// Accept / Decline post a verdict on the case AND file the accept/decline report
// (complete_work_order) so the party's answer is on the job; Ask… is a question
// back to the office (a verdict of "edit" with the question as the reason).
import { useState, type ReactNode } from "react";
import type { Case, Verdict } from "@artemis-group/aiw-contracts";
import { Button, Textarea, cn } from "@aiw/ui";
import type { CaseUiState } from "@aiw/ui";
import { useT } from "../i18n";
import { day, time } from "../lib/format";
import { Pill } from "./Panel";

export interface OfferCardProps {
  kase: Case;
  state: CaseUiState;
  busy?: boolean;
  onDecide: (decision: Verdict["decision"], extra?: { reason?: string | null }) => void | Promise<void>;
  className?: string;
}

function textOf(w: Case["questions"]["what"]): string {
  switch (w.kind) {
    case "email": return `${w.subject}`;
    case "record": return Object.entries(w.fields).filter(([, v]) => v != null && v !== "").map(([k, v]) => `${k.replace(/_/g, " ")} ${String(v)}`).join(" · ");
    default: return w.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

export function OfferCard({ kase, state, busy, onDecide, className }: OfferCardProps) {
  const { t, lang } = useT();
  const q = kase.questions;
  const [mode, setMode] = useState<"idle" | "ask" | "decline">("idle");
  const [reason, setReason] = useState("");
  const settled = state !== "proposed" && state !== "revised";
  const body = textOf(q.what);
  const [headline, ...rest] = body.split(/(?<=\.)\s+/);
  const options = [q.considered.reasoning, ...q.considered.rejected];
  const [choice, setChoice] = useState(0);

  const settledWords = state === "declined" ? t("offer_declined") : state === "revision requested" ? t("offer_asked") : state === "failed" ? t("offer_failed") : state === "expired" ? t("offer_expired") : t("offer_accepted");
  const settledTone = state === "failed" ? "bad" : state === "declined" || state === "expired" ? "plain" : state === "revision requested" ? "info" : "ok";

  return (
    <article className={cn("rounded-lg border border-l-4 border-l-rail bg-card text-card-foreground", className)} aria-label={t("new_job_offered")} data-offer-state={state}>
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pt-3 text-micro text-muted-foreground">
        <span className="font-medium text-foreground">{t("offer_from")}</span>
        <span>· {time(kase.created_at, lang)}</span>
        {kase.expires_at && !settled && <Pill tone="warn" className="ml-auto">{t("offer_answer_by", { when: day(kase.expires_at, lang) })}</Pill>}
        {settled && <Pill tone={settledTone} className="ml-auto">{settledWords}</Pill>}
      </header>

      <div className="px-4 pb-4 pt-2 text-sm">
        <h3 className="font-display text-[17px] font-medium leading-snug text-foreground">{headline}</h3>

        <div className="mt-3 space-y-3">
          {rest.length > 0 && <Block label={t("offer_what")}><p>{rest.join(" ")}</p></Block>}
          <Block label={t("offer_why")}><p>{q.why_now.trigger}</p></Block>
          <Block label={t("offer_options")}>
            <ul className="space-y-1" role="radiogroup">
              {options.map((o, i) => {
                const [title, ...detail] = o.split(" · ");
                return (
                  <li key={o}>
                    <label className={cn("flex min-h-[44px] cursor-pointer items-start gap-3 rounded-md border px-3 py-2", choice === i ? "border-primary/40 bg-accent/40" : "border-transparent")}>
                      <input type="radio" name={`opt-${kase.id}`} className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]" checked={choice === i} onChange={() => setChoice(i)} disabled={settled} />
                      <span className="min-w-0">
                        <span className="font-medium">{title}</span>
                        {detail.length > 0 && <span className="block text-muted-foreground">{detail.join(" · ")}</span>}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </Block>
          <div className="grid gap-3 sm:grid-cols-2">
            <Block label={t("offer_if_no")}><p>{q.if_no}</p></Block>
            <Block label={t("offer_undo")}><p>{q.undo.kind === "reversible" ? t("undo_reversible") : q.undo.kind === "with_effort" ? t("undo_with_effort") : t("undo_irreversible")}</p></Block>
          </div>
        </div>

        {!settled && mode !== "idle" && (
          <div className="mt-3 space-y-2 rounded-md border bg-background p-3">
            <label className="block font-medium" htmlFor={`reason-${kase.id}`}>{mode === "ask" ? t("ask_label") : t("decline_label")}</label>
            <Textarea id={`reason-${kase.id}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={mode === "ask" ? t("ask_placeholder") : t("decline_placeholder")} />
            <div className="grid grid-cols-2 gap-2">
              <Button size="touch" variant={mode === "ask" ? "default" : "destructive"} disabled={busy || (mode === "ask" && reason.trim().length < 3)} onClick={() => void onDecide(mode === "ask" ? "edit" : "reject", { reason: reason.trim() || null })}>
                {mode === "ask" ? t("send") : t("verb_decline")}
              </Button>
              <Button size="touch" variant="ghost" onClick={() => setMode("idle")}>{t("back")}</Button>
            </div>
          </div>
        )}

        <footer className="mt-3">
          {!settled ? (
            <div className="grid grid-cols-3 gap-2">
              <Button size="touch" disabled={busy || mode !== "idle"} onClick={() => void onDecide("approve", { reason: options[choice] ?? null })}>{busy ? t("offer_sending") : t("verb_accept")}</Button>
              <Button size="touch" variant="outline" disabled={busy} onClick={() => setMode("ask")}>{t("verb_ask")}</Button>
              <Button size="touch" variant="ghost" disabled={busy} onClick={() => setMode("decline")}>{t("verb_decline")}</Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{settledWords}</p>
          )}
        </footer>
      </div>
    </article>
  );
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="kicker mb-1">{label}</div>
      {children}
    </div>
  );
}
